import {useCallback, useEffect} from 'react';
import {useHistory} from 'react-router-dom';
import {useAlertTask} from '../../scenes/modals';

/**
 * Displays a warning alert with ok/cancel page if the user tries
 * to leave the page in any way.
 */
export const useWarnLeavePage = (
  title: string,
  body: string,
  condition: () => boolean,
  callback?: () => void,
) => {
  const alert = useAlertTask();
  const history = useHistory();
  const isEditing = condition();

  // We'll listen to the react router's history object
  useEffect(() => {
    const unblock = history.block(toLocation => {
      if (isEditing) {
        alert({
          title,
          body,
        }).then(result => {
          if (result) {
            unblock();
            callback && callback();
            history.push(toLocation);
          }
        });
        return false;
      }
    });

    return () => {
      unblock();
    };
  }, [alert, body, callback, history, isEditing, title]);

  // Let's hook onto beforeUnload on page as well
  const handleExternalLeave = useCallback(
    (evt: any) => {
      if (isEditing) {
        evt.returnValue = body;
      } else {
        callback && callback();
      }
    },
    [body, callback, isEditing],
  );

  useEffect(() => {
    window.addEventListener('beforeunload', handleExternalLeave);
    return () => {
      window.removeEventListener('beforeunload', handleExternalLeave);
    };
  }, [handleExternalLeave]);
};
